/**
 * Firebase Phone Auth helpers (web Recaptcha + Capacitor native).
 * Replaces custom Cloud Function OTP for client login/signup.
 */

import { Capacitor } from '@capacitor/core';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
} from 'firebase/auth';
import { auth } from '../firebase.config';

const PHONE_AUTH_SESSION_KEY = 'acctoo_phone_auth_session_v1';
/** Firebase phone sessions are short-lived; keep restore window conservative */
const PHONE_AUTH_SESSION_TTL_MS = 10 * 60 * 1000;

/** @type {RecaptchaVerifier | null} */
let webRecaptcha = null;

/** @type {{ remove: () => Promise<void> } | null} */
let nativeCodeSentListener = null;

/** @type {{ remove: () => Promise<void> } | null} */
let nativeVerificationFailedListener = null;

export function normalizeOtpCode(code) {
  return String(code || '').replace(/\D/g, '').slice(0, 6);
}

export function toE164Phone(phone, defaultCountry = '91') {
  const raw = String(phone || '').trim();
  if (!raw) return '';
  if (raw.startsWith('+')) {
    return `+${raw.slice(1).replace(/\D/g, '')}`;
  }
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+${defaultCountry}${digits}`;
  if (digits.startsWith(defaultCountry) && digits.length === 12) return `+${digits}`;
  return digits ? `+${digits}` : '';
}

function persistPhoneAuthSession(payload) {
  try {
    sessionStorage.setItem(
      PHONE_AUTH_SESSION_KEY,
      JSON.stringify({ ...payload, savedAt: Date.now() })
    );
  } catch {
    /* ignore quota / private mode */
  }
}

export function clearPhoneAuthSession() {
  try {
    sessionStorage.removeItem(PHONE_AUTH_SESSION_KEY);
  } catch {
    /* ignore */
  }
}

async function removeNativePhoneListeners() {
  if (nativeCodeSentListener) {
    try {
      await nativeCodeSentListener.remove();
    } catch {
      /* ignore */
    }
    nativeCodeSentListener = null;
  }
  if (nativeVerificationFailedListener) {
    try {
      await nativeVerificationFailedListener.remove();
    } catch {
      /* ignore */
    }
    nativeVerificationFailedListener = null;
  }
}

function buildNativeConfirm(verificationId, e164) {
  return async (code) => {
    const otp = normalizeOtpCode(code);
    if (otp.length !== 6) {
      const err = new Error('Please enter the 6-digit code from your latest SMS.');
      err.code = 'auth/invalid-verification-code';
      throw err;
    }

    const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
    try {
      const result = await FirebaseAuthentication.confirmVerificationCode({
        verificationId,
        verificationCode: otp,
      });
      if (!auth.currentUser && result?.credential?.verificationId) {
        const credential = PhoneAuthProvider.credential(result.credential.verificationId, otp);
        await signInWithCredential(auth, credential);
      } else if (!auth.currentUser && verificationId) {
        const credential = PhoneAuthProvider.credential(verificationId, otp);
        await signInWithCredential(auth, credential);
      }
      clearPhoneAuthSession();
      return { user: auth.currentUser, phone: e164 };
    } catch (error) {
      throw normalizeNativePhoneError(error);
    }
  };
}

function normalizeNativePhoneError(error) {
  const message = String(error?.message || error || '');
  const codeFromMessage = message.match(/auth\/[a-z-]+/i)?.[0];
  const err = new Error(message || 'Phone verification failed');
  err.code = error?.code || codeFromMessage || 'auth/invalid-verification-code';
  if (/expired/i.test(message)) err.code = 'auth/code-expired';
  if (/invalid.*verification.*code/i.test(message)) err.code = 'auth/invalid-verification-code';
  return err;
}

/**
 * Restore in-memory confirm session after WebView reload (native OTP).
 */
export function restorePhoneAuthSession(expectedPhone) {
  try {
    const raw = sessionStorage.getItem(PHONE_AUTH_SESSION_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data?.verificationId || !data?.phone) return null;
    if (Date.now() - (data.savedAt || 0) > PHONE_AUTH_SESSION_TTL_MS) {
      clearPhoneAuthSession();
      return null;
    }
    const expected = toE164Phone(expectedPhone);
    if (expected && data.phone !== expected) return null;

    if (data.platform === 'native') {
      return {
        platform: 'native',
        phone: data.phone,
        verificationId: data.verificationId,
        confirm: buildNativeConfirm(data.verificationId, data.phone),
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function clearPhoneRecaptcha() {
  try {
    if (webRecaptcha) {
      webRecaptcha.clear();
    }
  } catch {
    /* ignore */
  }
  webRecaptcha = null;
}

async function ensureWebRecaptcha(containerId = 'new-login-recaptcha') {
  if (typeof window === 'undefined') {
    throw new Error('Phone auth requires a browser environment');
  }

  let el = document.getElementById(containerId);
  if (!el) {
    el = document.createElement('div');
    el.id = containerId;
    el.style.display = 'none';
    document.body.appendChild(el);
  }

  if (webRecaptcha) {
    try {
      return webRecaptcha;
    } catch {
      clearPhoneRecaptcha();
    }
  }

  webRecaptcha = new RecaptchaVerifier(auth, containerId, {
    size: 'invisible',
    callback: () => {},
    'expired-callback': () => {
      clearPhoneRecaptcha();
    },
  });

  await webRecaptcha.render();
  return webRecaptcha;
}

async function sendNativePhoneOtp(e164) {
  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');

  await removeNativePhoneListeners();
  clearPhoneAuthSession();

  const codeSentPromise = new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(Object.assign(new Error('Timed out waiting for SMS. Please try again.'), { code: 'auth/session-expired' }));
    }, 120000);

    const finish = (fn) => {
      clearTimeout(timeout);
      fn();
    };

    FirebaseAuthentication.addListener('phoneCodeSent', (event) => {
      if (!event?.verificationId) return;
      finish(() => resolve(event.verificationId));
    }).then((handle) => {
      nativeCodeSentListener = handle;
    });

    FirebaseAuthentication.addListener('phoneVerificationFailed', (event) => {
      finish(() => reject(normalizeNativePhoneError(event)));
    }).then((handle) => {
      nativeVerificationFailedListener = handle;
    });
  });

  await FirebaseAuthentication.signInWithPhoneNumber({ phoneNumber: e164 });
  const verificationId = await codeSentPromise;
  await removeNativePhoneListeners();

  persistPhoneAuthSession({
    platform: 'native',
    phone: e164,
    verificationId,
  });

  return {
    platform: 'native',
    verificationId,
    phone: e164,
    confirm: buildNativeConfirm(verificationId, e164),
  };
}

async function sendWebPhoneOtp(e164, containerId) {
  clearPhoneRecaptcha();
  const verifier = await ensureWebRecaptcha(containerId);
  const confirmation = await signInWithPhoneNumber(auth, e164, verifier);
  return {
    platform: 'web',
    confirmation,
    phone: e164,
    confirm: async (code) => {
      const otp = normalizeOtpCode(code);
      const result = await confirmation.confirm(otp);
      clearPhoneAuthSession();
      return { user: result.user, phone: e164 };
    },
  };
}

/**
 * Start Firebase Phone Auth SMS.
 * @returns {Promise<{ platform: string, phone: string, confirm: (code: string) => Promise<{user, phone}> }>}
 */
export async function startFirebasePhoneAuth(phone, options = {}) {
  const e164 = toE164Phone(phone);
  if (!/^\+[1-9]\d{7,14}$/.test(e164)) {
    const err = new Error('Invalid phone number. Use a valid mobile number with country code.');
    err.code = 'auth/invalid-phone-number';
    throw err;
  }

  if (Capacitor.isNativePlatform()) {
    try {
      return await sendNativePhoneOtp(e164);
    } catch (nativeErr) {
      console.warn('Native phone auth failed, falling back to web Auth:', nativeErr);
    }
  }

  return sendWebPhoneOtp(e164, options.containerId || 'new-login-recaptcha');
}

export function getPhoneAuthErrorMessage(error) {
  const code = error?.code || '';
  const map = {
    'auth/invalid-phone-number': 'Invalid phone number format.',
    'auth/missing-phone-number': 'Please enter a phone number.',
    'auth/quota-exceeded': 'SMS quota exceeded. Try again later or use email/Google.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/operation-not-allowed': 'Phone sign-in is not enabled. Enable Phone in Firebase Authentication.',
    'auth/too-many-requests': 'Too many SMS attempts. Wait a few minutes, or verify with email instead.',
    'auth/invalid-verification-code':
      'Invalid OTP. Use the code from your latest SMS (tap Resend if you requested a new one).',
    'auth/code-expired':
      'This OTP is no longer valid. Tap Resend OTP and enter the new code from your latest SMS.',
    'auth/session-expired':
      'Verification session expired. Tap Resend OTP and try again.',
    'auth/captcha-check-failed': 'reCAPTCHA failed. Refresh the page and try again.',
    'auth/missing-verification-code': 'Please enter the OTP.',
    'auth/invalid-app-credential':
      'Phone SMS is not configured for this app. Use email verification for now, or ask support to enable Phone Auth (authorized domain + reCAPTCHA).',
    'auth/credential-already-in-use': 'That phone number is already linked to another account.',
    'auth/provider-already-linked': 'A phone number is already linked to this account.',
  };
  if (map[code]) return map[code];
  if (typeof error?.message === 'string' && error.message.includes('invalid-app-credential')) {
    return map['auth/invalid-app-credential'];
  }
  if (typeof error?.message === 'string' && error.message.length < 160) return error.message;
  return 'Phone verification failed. Please try again or use email/Google.';
}

export function isFirebasePhoneVerified(user = auth.currentUser) {
  if (!user) return false;
  return !!(
    user.phoneNumber ||
    user.providerData?.some((p) => p.providerId === 'phone')
  );
}

/**
 * SMS footer text is controlled in Firebase/Google Cloud (public-facing name / auth domain).
 * @see https://firebase.google.com/docs/auth/web/custom-auth-domain
 */
export const PHONE_SMS_BRANDING_CONSOLE_HINT =
  'Firebase Console → Project settings → Public-facing name: Acctoo; add app.acctoo.com under Authentication → Authorized domains. Optional: custom auth domain for SMS.';
